// TODUE V2 — SQLite adapter (jednotné store API nad engine).
// Cieľ: nahradiť priame Dexie `db.<store>.*` volania per-store proxy s ~rovnakou
// syntaxou (db.records.add/get/update/where().equals().filter().toArray()).
//
// Rieši:
//   • JSON (de)serializáciu vnorených polí (items/counterparty/raw/value…),
//   • per-store PK (vendors=ico, settings=key, ostatné=id),
//   • soft-delete + LWW polia (rev/updated_at/created_at) ostávajú ako stĺpce,
//     merge ostáva v app vrstve — adapter ich len verne perzistuje,
//   • kernel markery (schema_version/obligation_pack_version) cez DDL DEFAULT,
//   • atomický counter cez transaction() s reentrant guardom,
//   • onChange emitter (náhrada Dexie hooks pre driveSync auto-sync).
//
// Spec: MasterProject/workflows/todue-v2-mythos/SQLITE-MIGRATION-SPEC.md §1

import { PK, JSON_COLUMNS, BOOL_COLUMNS, STORES, HLC_STORES } from './schema';
import { stampAdd, stampUpdate } from '../sync/hlcRecord';

// ─── JSON (de)serializácia per-store ─────────────────────────────────────────
function jsonCols(store) {
  return JSON_COLUMNS[store] || [];
}

function boolCols(store) {
  return BOOL_COLUMNS[store] || [];
}

// row z appky → row pre SQL (JSON polia stringify, null-safe, undefined→null).
function serializeRow(store, row) {
  const cols = jsonCols(store);
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    if (v === undefined) {
      out[k] = null;
    } else if (cols.includes(k)) {
      // JSON stĺpec: null ostáva null, inak stringify (aj int/bool — settings.value polymorf).
      out[k] = v === null ? null : JSON.stringify(v);
    } else if (typeof v === 'boolean') {
      out[k] = v ? 1 : 0; // booleans → INTEGER (tax_relevant a pod.)
    } else if (v !== null && typeof v === 'object') {
      // Neoznačený objekt v scalar stĺpci by inak padol — stringify obranne.
      out[k] = JSON.stringify(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

// row zo SQL → row pre appku (JSON polia parse, null-safe).
function deserializeRow(store, row) {
  if (!row) return row;
  const cols = jsonCols(store);
  const out = { ...row };
  for (const c of cols) {
    if (c in out) {
      const raw = out[c];
      if (raw === null || raw === undefined) {
        out[c] = null;
      } else if (typeof raw === 'string') {
        try { out[c] = JSON.parse(raw); } catch { out[c] = raw; }
      }
    }
  }
  // Boolean stĺpce: 1/0 (INTEGER v SQL) → true/false (Dexie držal natívny bool).
  // null/undefined necháme tak (nie je to false).
  for (const c of boolCols(store)) {
    if (c in out) {
      const raw = out[c];
      if (raw !== null && raw !== undefined) out[c] = !!raw;
    }
  }
  return out;
}

function deserializeRows(store, rows) {
  return (rows || []).map((r) => deserializeRow(store, r));
}

// ─── Store factory ───────────────────────────────────────────────────────────
// engineRef = funkcia ktorá vráti aktuálny engine (lazy singleton); execTarget
// umožní transakcii podsunúť tx-bound engine handle (single connection → ten istý).
function makeStore(name, getExec, emit) {
  const pk = PK[name];

  function buildInsert(row) {
    const ser = serializeRow(name, row);
    const cols = Object.keys(ser);
    const placeholders = cols.map(() => '?').join(',');
    const sql = `INSERT INTO ${name} (${cols.join(',')}) VALUES (${placeholders})`;
    return { sql, params: cols.map((c) => ser[c]) };
  }

  function buildUpsert(row) {
    const ser = serializeRow(name, row);
    const cols = Object.keys(ser);
    const placeholders = cols.map(() => '?').join(',');
    const updateCols = cols.filter((c) => c !== pk);
    const setClause = updateCols.map((c) => `${c}=excluded.${c}`).join(',');
    const sql = setClause
      ? `INSERT INTO ${name} (${cols.join(',')}) VALUES (${placeholders}) ON CONFLICT(${pk}) DO UPDATE SET ${setClause}`
      : `INSERT INTO ${name} (${cols.join(',')}) VALUES (${placeholders}) ON CONFLICT(${pk}) DO NOTHING`;
    return { sql, params: cols.map((c) => ser[c]) };
  }

  const store = {
    _name: name,
    name, // Dexie-parity: backup.js iteruje db.tables a číta table.name
    _pk: pk,

    async get(id) {
      const e = getExec();
      const row = await e.get(`SELECT * FROM ${name} WHERE ${pk} = ?`, [id]);
      return row ? deserializeRow(name, row) : undefined;
    },

    async add(row) {
      const e = getExec();
      // Per-field CRDT mint (Fáza 2): nový záznam HLC_STORES tabuľky dostane `_hlc` pre všetky
      // content polia. `put` (sync víťaz) sa NEstampuje — nesie `_hlc` z merge.
      // Skip ak už má `_hlc` (idempotentné). Chyba (napr. HLC node nenastavený v teste/boote) =
      // bez `_hlc` → merge padne na LWW (Fable dizajn, žiadna strata).
      if (HLC_STORES.has(name) && row && !row._hlc) {
        try { stampAdd(row); } catch { /* HLC nepripravené → LWW fallback */ }
      }
      const { sql, params } = buildInsert(row);
      await e.run(sql, params);
      emit(name, 'creating', row[pk]);
      return row[pk];
    },

    async put(row) {
      const e = getExec();
      const { sql, params } = buildUpsert(row);
      await e.run(sql, params);
      emit(name, 'updating', row[pk]);
      return row[pk];
    },

    async update(id, patch) {
      const e = getExec();
      // Per-field CRDT mint (Fáza 2 commit 3): opečiatkuj zmenené content polia, zachovaj pečiatky
      // ostatných z existujúceho záznamu (načítaj len `_hlc`+LWW triplet, nie celý row). Skip ak
      // patch už `_hlc` nesie. Chyba → bez `_hlc` → LWW fallback.
      if (HLC_STORES.has(name) && patch && !patch._hlc) {
        try {
          const ex = await e.get(`SELECT _hlc, updated_at, rev, deleted_at FROM ${name} WHERE ${pk} = ?`, [id]);
          const existingRow = ex
            ? { _hlc: ex._hlc ? JSON.parse(ex._hlc) : null, updated_at: ex.updated_at, rev: ex.rev, deleted_at: ex.deleted_at }
            : null;
          stampUpdate(existingRow, patch);
        } catch { /* HLC nepripravené → LWW fallback */ }
      }
      // Dexie sémantika: kľúče s hodnotou undefined sa IGNORUJÚ (nie zapíšu ako NULL),
      // inak by patch s undefined poľom prepísal existujúci stĺpec na NULL.
      const defined = {};
      for (const [k, v] of Object.entries(patch || {})) {
        if (v !== undefined) defined[k] = v;
      }
      const ser = serializeRow(name, defined);
      const cols = Object.keys(ser);
      if (cols.length === 0) return 0;
      const setClause = cols.map((c) => `${c}=?`).join(',');
      const res = await e.run(
        `UPDATE ${name} SET ${setClause} WHERE ${pk} = ?`,
        [...cols.map((c) => ser[c]), id]
      );
      emit(name, 'updating', id);
      return res.changes;
    },

    async delete(id) {
      const e = getExec();
      const res = await e.run(`DELETE FROM ${name} WHERE ${pk} = ?`, [id]);
      emit(name, 'deleting', id);
      return res.changes;
    },

    async bulkAdd(rows) {
      // Atomicky: všetko alebo nič. getExec() vracia tx-scoped handle keď sme už
      // vnútri db.transaction (→ jeho .transaction robí SAVEPOINT, žiadny mutex),
      // inak globálny engine (→ top-level mutex). Bezpečné aj vnorené aj samostatne.
      const e = getExec();
      const list = rows || [];
      const keys = list.map((row) => row[pk]);
      if (list.length === 0) return keys;
      await e.transaction(async (tx) => {
        for (const row of list) {
          const { sql, params } = buildInsert(row);
          await tx.run(sql, params);
        }
      });
      emit(name, 'creating', null);
      return keys;
    },

    async bulkPut(rows) {
      const e = getExec();
      const list = rows || [];
      const keys = list.map((row) => row[pk]);
      if (list.length === 0) return keys;
      await e.transaction(async (tx) => {
        for (const row of list) {
          const { sql, params } = buildUpsert(row);
          await tx.run(sql, params);
        }
      });
      emit(name, 'updating', null);
      return keys;
    },

    async clear() {
      const e = getExec();
      const res = await e.run(`DELETE FROM ${name}`, []);
      emit(name, 'deleting', null);
      return res.changes;
    },

    async count() {
      const e = getExec();
      const row = await e.get(`SELECT COUNT(*) AS c FROM ${name}`, []);
      return Number(row?.c ?? 0);
    },

    async toArray() {
      const e = getExec();
      const rows = await e.all(`SELECT * FROM ${name}`, []);
      return deserializeRows(name, rows);
    },

    orderBy(col) {
      return {
        async toArray() {
          const e = getExec();
          const rows = await e.all(`SELECT * FROM ${name} ORDER BY ${col} ASC`, []);
          return deserializeRows(name, rows);
        },
      };
    },

    // Dexie-parity JS filter (filter ostáva JS → 0 riziko regresie, 1:1 sémantika).
    filter(fn) {
      return {
        async toArray() {
          const all = await store.toArray();
          return all.filter(fn);
        },
      };
    },

    // ── Query builder: where(col).equals(val)[.filter(fn)][.first()|.toArray()] ──
    where(col) {
      return {
        equals(val) {
          // null/undefined → `IS NULL` (SQL `= NULL` je vždy false, ticho prázdny výsledok).
          // boolean → 1/0 aby param sedel s uloženým INTEGER (Dexie držal natívny bool).
          const isNull = val === null || val === undefined;
          const param = typeof val === 'boolean' ? (val ? 1 : 0) : val;
          const baseSql = isNull
            ? `SELECT * FROM ${name} WHERE ${col} IS NULL`
            : `SELECT * FROM ${name} WHERE ${col} = ?`;
          const args = isNull ? [] : [param];
          const builder = {
            async toArray() {
              const e = getExec();
              const rows = await e.all(baseSql, args);
              return deserializeRows(name, rows);
            },
            async first() {
              const e = getExec();
              const row = await e.get(`${baseSql} LIMIT 1`, args);
              return row ? deserializeRow(name, row) : undefined;
            },
            filter(fn) {
              return {
                async toArray() {
                  const rows = await builder.toArray();
                  return rows.filter(fn);
                },
                async first() {
                  const rows = await builder.toArray();
                  return rows.filter(fn)[0];
                },
                async sortBy(sortCol) {
                  const rows = (await builder.toArray()).filter(fn);
                  return rows.sort((a, b) => (a[sortCol] > b[sortCol] ? 1 : a[sortCol] < b[sortCol] ? -1 : 0));
                },
              };
            },
            async sortBy(sortCol) {
              const rows = await builder.toArray();
              return rows.sort((a, b) => (a[sortCol] > b[sortCol] ? 1 : a[sortCol] < b[sortCol] ? -1 : 0));
            },
          };
          return builder;
        },
      };
    },
  };

  return store;
}

// ─── createDb() — postaví `db` objekt so store proxy + tx + onChange ─────────
export function createDb(getEngine, ensureOpen) {
  const listeners = new Set();
  function fire(store, op, key) {
    for (const fn of listeners) {
      try { fn(store, op, key); } catch { /* listener nesmie zhodiť write */ }
    }
  }

  // ── Tx-scoped handle (race fix) ──────────────────────────────────────────────
  // Pôvodne bol "aktuálny tx engine" globálna/ALS mutable premenná čítaná PRED
  // mutexom. Pri async cb (await vnútri tx) ju nezávislá PARALELNÁ top-level tx
  // prečítala cez await hranicu ako svoju → savepoint na cudzej tx → "no such
  // savepoint" / lost update. V Node to maskoval AsyncLocalStorage; v browseri
  // (žiadny spoľahlivý ALS) ostal reziduálny race.
  //
  // Riešenie (Prisma/Drizzle pattern): `db.transaction(cb)` odovzdá `cb` tx-scoped
  // `tx` handle, ktorého stores sú VIAZANÉ na tx engine cez closure — nie na globál
  // ani ALS. Vnorené operácie idú cez `tx.<store>` → tá istá tx connection. Funguje
  // identicky Node + browser bez async_hooks a škáluje pre rastúcu súbežnosť
  // (autosync + inbox poll + recurring cron + multi-device) bez tichého race rizika.
  //
  // Globálne stores (mimo transakcie) → onChange fire OKAMŽITE. Pod Direction A
  // kontraktom globálny store NIKDY nebeží vnútri tx (tam sa používa tx handle),
  // takže priame fire je vždy správne. Žiadny globálny emit buffer — ten mal
  // rovnaký súbeh-race ako tx mutex: paralelná top-level tx by videla globálny
  // buffer ako "svoj nested" a emitla by pred commitom / prežila by rollback
  // (Codex nález 2026-07-17). Buffer je preto TX-LOKÁLNY (nižšie).
  const stores = {};
  for (const name of STORES) {
    stores[name] = makeStore(name, getEngine, fire);
  }

  // Postaví tx-scoped `db` handle viazaný na konkrétny tx engine. Jeho stores
  // operujú cez `txEngine` (bežiaca BEGIN..COMMIT / SAVEPOINT scope). Nested
  // `tx.transaction(cb)` → SAVEPOINT na txEngine (žiadny mutex → žiadny self-deadlock).
  // Emity idú do TX-LOKÁLNEHO `buffer` (nie globál) → flush až po outer commite;
  // nested savepoint má vlastný child buffer (merge do parent po RELEASE, zahodenie
  // po ROLLBACK TO — zrušené zmeny sa nesmú notifikovať).
  function makeTxDb(txEngine, buffer) {
    const txEmit = (store, op, key) => buffer.push([store, op, key]);
    const txStores = {};
    for (const name of STORES) txStores[name] = makeStore(name, () => txEngine, txEmit);
    return {
      ...txStores,
      table(name) { return txStores[name]; },
      get tables() { return STORES.map((n) => txStores[n]); },
      async transaction(...args) {
        const cb = args[args.length - 1];
        // Vnorená (SAVEPOINT) tx: vlastný child buffer. Po úspechu (RELEASE) prelej
        // do parent bufferu; pri chybe (ROLLBACK TO) txEngine.transaction re-throwne
        // → merge sa nevykoná → child emity sa zahodia (rollbacknuté zmeny netreba syncovať).
        const childBuffer = [];
        const result = await txEngine.transaction((nestedEngine) => cb(makeTxDb(nestedEngine, childBuffer)));
        for (const e of childBuffer) buffer.push(e);
        return result;
      },
    };
  }

  const db = {
    ...stores,

    // dynamic store-by-name (driveSync SYNC_STORES loop)
    table(name) { return stores[name]; },
    get tables() { return STORES.map((n) => stores[n]); },

    // Dexie-kompatibilný transaction podpis: transaction('rw', t1, t2, cb)
    // alebo transaction(cb). `cb` dostane tx-scoped `tx` handle — vnútri MUSÍŠ
    // používať `tx.<store>` (nie globálny `db`). Globálny `db` op vnútri tx callbacku
    // = SELF-DEADLOCK by design (čaká na engine mutex, ktorý drží táto tx) —
    // engine watchdog to nahlas zdiagnostikuje. Pod Direction A je to kontrakt-porušenie.
    //
    // VŽDY top-level cez engine mutex (BEGIN IMMEDIATE..COMMIT) — serializuje
    // nezávislé paralelné tx. Skutočne vnorená tx ide cez `tx.transaction(cb2)` →
    // SAVEPOINT na bežiacom tx engine (žiadny mutex → žiadny self-deadlock).
    async transaction(...args) {
      const cb = args[args.length - 1];
      const engine = getEngine();
      // TX-LOKÁLNY emit buffer (Codex 2026-07-17): každá top-level tx má vlastný,
      // takže paralelné tx si emity nezmiešajú a rollback zahodí len svoje. Flush
      // AŽ po commite — driveSync auto-sync tak nikdy nečíta nezakomitovaný/zrušený stav.
      const buffer = [];
      const result = await engine.transaction((txEngine) => cb(makeTxDb(txEngine, buffer)));
      // commit OK → až teraz doruč onChange v poradí zápisu. Pri rollbacku
      // engine.transaction hodí → sem sa nedostaneme → buffer sa zahodí.
      for (const [s, o, k] of buffer) fire(s, o, k);
      return result;
    },

    // onChange emitter (náhrada Dexie table.hook pre driveSync auto-sync)
    onChange(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    // init guards (backup.js, initV2). Dexie má isOpen() ako METÓDU (backup.js
    // ho volá ako db.isOpen()). ensureOpen.isOpen ostáva getter (db.js:31).
    isOpen() { return ensureOpen ? ensureOpen.isOpen : true; },
    async open() { return ensureOpen ? ensureOpen.open() : undefined; },
    verno: undefined, // doplní db.js na SCHEMA_VERSION
  };

  return db;
}

export { serializeRow, deserializeRow };
